import { IDBPDatabase, openDB } from "idb";
import { AnswerMode } from "../answer-modes/AnswerModeContainer/AnswerMode";
import { Answers, HistoryMetaData } from "../../api/models";

const sanitizeAnswerModes = (
  answerModes: unknown,
): { answerModes: AnswerMode[]; didMigrate: boolean } => {
  if (!Array.isArray(answerModes)) {
    return { answerModes: [], didMigrate: false };
  }

  let didMigrate = false;
  const sanitized = answerModes.map((mode) => {
    if (!mode || typeof mode !== "object") {
      return mode as AnswerMode;
    }

    const modeRecord = mode as Record<string, unknown>;
    if (!Object.prototype.hasOwnProperty.call(modeRecord, "temperature")) {
      return mode as AnswerMode;
    }

    didMigrate = true;
    const { temperature: _temperature, ...modeWithoutTemperature } = modeRecord;
    return modeWithoutTemperature as AnswerMode;
  });

  return { answerModes: sanitized, didMigrate };
};

export class IndexedDB {
  private dbName: string;
  private storeName: string;
  private dbPromise: Promise<IDBPDatabase> | null = null;
  private cursorKey: IDBValidKey | undefined;
  private isCursorEnd: boolean = false;
  private useCase: string;

  constructor(dbName: string, storeName: string, useCase: string) {
    this.dbName = dbName;
    this.storeName = storeName;
    this.useCase = useCase;
    this.cursorKey = undefined;
    this.isCursorEnd = false;
  }

  // Initializes the IndexedDB, creating the database and object store if they don't exist.
  // Sets up the necessary indexes for efficient querying.
  private async init() {
    const storeName = this.storeName;
    if (!this.dbPromise) {
      this.dbPromise = openDB(this.dbName, 1, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(storeName)) {
            const store = db.createObjectStore(storeName, { keyPath: "id" });
            // Creates an index on the 'timestamp' field for efficient sorting and querying by time.
            store.createIndex("timestamp", "timestamp");
          }
        },
      });
    }
    return this.dbPromise;
  }

  // Resets the cursor state for pagination, allowing retrieval from the beginning.
  resetContinuationToken() {
    this.cursorKey = undefined;
    this.isCursorEnd = false;
  }

  // Adds or updates an item in the IndexedDB with the given ID and answers.
  // Sets the current timestamp and handles both new and existing entries.
  async addItem(
    id: string,
    answers: Answers,
    answerMode: AnswerMode[],
    filepaths: Array<string> = [],
    filenames: Array<string> = [],
    image_filenames: Array<string> = [],
    title?: string,
    error?: string,
    stateSnapshot?: Record<string, any> | null, // ADD
    selectedSites?: string[],
  ): Promise<void> {
    const timestamp = Date.now();
    const db = await this.init();
    const tx = db.transaction(this.storeName, "readwrite");
    const currentChat = await tx.objectStore(this.storeName).get(id);
    const { answerModes: sanitizedIncomingAnswerModes } =
      sanitizeAnswerModes(answerMode);

    if (currentChat) {
      const existingDocuments = Array.isArray(currentChat.documents)
        ? currentChat.documents
        : [];
      const existingFilenames = Array.isArray(currentChat.filenames)
        ? currentChat.filenames
        : [];
      const existingImages = Array.isArray(currentChat.image_filenames)
        ? currentChat.image_filenames
        : [];

      const updatedDocuments = [
        ...new Set([...existingDocuments, ...filepaths]),
      ];
      const updatedFilenames = [
        ...new Set([...existingFilenames, ...filenames]),
      ];
      const updatedImages = [
        ...new Set([...existingImages, ...image_filenames]),
      ];

      const updatedTitle =
        title && title.trim() !== "" ? title : currentChat.title;
      const { answerModes: sanitizedStoredAnswerModes } =
        sanitizeAnswerModes(currentChat.answerMode);

      await tx.objectStore(this.storeName).put({
        ...currentChat,
        id,
        timestamp,
        answers: answers.length > 0 ? answers : currentChat.answers,
        answerMode:
          sanitizedIncomingAnswerModes.length > 0
            ? sanitizedIncomingAnswerModes
            : sanitizedStoredAnswerModes,
        documents: updatedDocuments,
        filenames: updatedFilenames,
        image_filenames: updatedImages,
        title: updatedTitle,
        error: error || null,
        // Only overwrite if we got a new snapshot, otherwise keep existing
        stateSnapshot:
          stateSnapshot !== undefined
            ? stateSnapshot
            : (currentChat.stateSnapshot ?? null),
        selectedSites:
          selectedSites !== undefined
            ? selectedSites
            : (currentChat.selectedSites ?? ["auto"]),
      });
    } else {
      const newTitle =
        title && title.trim() !== ""
          ? title
          : answers[0][0].length > 50
            ? answers[0][0].substring(0, 50) + "..."
            : answers[0][0];

      await tx.objectStore(this.storeName).add({
        id,
        title: newTitle,
        timestamp,
        answers,
        documents: filepaths,
        filenames,
        image_filenames,
        answerMode: sanitizedIncomingAnswerModes,
        useCase: this.useCase,
        error: error || null,
        stateSnapshot: stateSnapshot ?? null, // ADD
        selectedSites: selectedSites ?? ["auto"],
      });
    }
  }

  /** Set the title of an existing chat without touching its answers. */
  async updateTitle(id: string, title: string): Promise<void> {
    if (!title.trim()) return;
    const db = await this.init();
    const tx = db.transaction(this.storeName, "readwrite");
    const chat = await tx.objectStore(this.storeName).get(id);
    if (chat) await tx.objectStore(this.storeName).put({ ...chat, title });
    await tx.done;
  }

  async getChat(id: string): Promise<any> {
    const db = await this.init();
    const tx = db.transaction(this.storeName, "readwrite");
    const currentChat = await tx.objectStore(this.storeName).get(id);

    return currentChat;
  }

  async getNextItems(count: number): Promise<HistoryMetaData[]> {
    // 1. If we've already reached the end, don't query again
    if (this.isCursorEnd) {
      return [];
    }

    const db = await this.init();
    const tx = db.transaction(this.storeName, "readonly");
    const store = tx.objectStore(this.storeName);
    const index = store.index("timestamp");

    // 2. Decide where to start (Pagination Logic)
    // We iterate 'prev' (newest first).
    // If we have a cursorKey, we want items strictly older (smaller timestamp), so use upperBound(key, true).
    let range: IDBKeyRange | null = null;
    if (this.cursorKey !== undefined) {
      range = IDBKeyRange.upperBound(this.cursorKey, true); // true = exclude current key
    }

    let cursor = await index.openCursor(range, "prev");

    const loadedItems: HistoryMetaData[] = [];

    // 3. Iterate
    while (loadedItems.length < count && cursor) {
      const value = cursor.value;

      if (value.useCase?.toLowerCase() === this.useCase.toLowerCase()) {
        // Calculate counts for UI
        const messageCount = Array.isArray(value.answers)
          ? value.answers.length
          : 0;

        const rawTotalDocs = Array.isArray(value.documents)
          ? value.documents.length
          : 0;
        const imageCount = Array.isArray(value.image_filenames)
          ? value.image_filenames.length
          : 0;

        // FIX: 'documents' usually contains ALL files (images + docs).
        // To avoid double counting, we subtract the images from the total document count.
        // We use Math.max(0, ...) just to be safe against data inconsistencies.
        const fileCount = Math.max(0, rawTotalDocs - imageCount);

        loadedItems.push({
          id: value.id,
          title: value.title,
          timestamp: value.timestamp,
          useCase: value.useCase,
          messageCount,
          fileCount, // Now represents ONLY non-image documents
          imageCount,
        });

        // Update the token to the last item added
        this.cursorKey = value.timestamp;
      }

      cursor = await cursor.continue();
    }

    // If cursor is null, we exhausted the DB
    if (!cursor) {
      this.isCursorEnd = true;
    }

    return loadedItems;
  }

  // Retrieves a specific item by its ID from the IndexedDB.
  // Returns the answers associated with the given ID, or null if not found.
  // Ensures efficient access to individual records for detailed data retrieval.
  async getItem(id: string): Promise<{
    answers: Answers | null;
    answerMode: AnswerMode[];
    error: string | null;
    stateSnapshot: Record<string, any> | null; // ADD
    selectedSites: string[];
  }> {
    const db = await this.init();
    const tx = db.transaction(this.storeName, "readwrite");
    const store = tx.objectStore(this.storeName);
    const item = await store.get(id);

    const isMatch =
      item && item.useCase.toLowerCase() === this.useCase.toLowerCase();

    if (!isMatch) {
      return {
        answers: null,
        answerMode: [],
        error: null,
        stateSnapshot: null,
        selectedSites: ["auto"],
      };
    }

    const { answerModes: sanitizedAnswerModes, didMigrate } =
      sanitizeAnswerModes(item.answerMode);

    // One-time read migration for legacy persisted answer modes.
    if (didMigrate) {
      await store.put({
        ...item,
        answerMode: sanitizedAnswerModes,
      });
    }

    await tx.done;

    return {
      answers: item.answers ?? null,
      answerMode: sanitizedAnswerModes,
      error: item.error || null,
      stateSnapshot: item.stateSnapshot || null, // ADD
      selectedSites:
        Array.isArray(item.selectedSites)
          ? item.selectedSites
          : ["auto"],
    };
  }

  async getDocs(id: string): Promise<{
    documents: string[];
    filenames: string[];
    image_filenames: string[];
  } | null> {
    const db = await this.init();
    const tx = db.transaction(this.storeName, "readonly");
    const item = await tx.objectStore(this.storeName).get(id);

    if (!item || item.useCase.toLowerCase() !== this.useCase.toLowerCase()) {
      return null;
    }

    // Backward-compatible defaults
    const documents = Array.isArray(item.documents) ? item.documents : [];
    const filenames = Array.isArray(item.filenames) ? item.filenames : [];
    const image_filenames = Array.isArray(item.image_filenames)
      ? item.image_filenames
      : [];

    return {
      documents,
      filenames,
      image_filenames,
    };
  }

  // Deletes a specific item by its ID from the IndexedDB.
  // Ensures efficient removal of individual records.
  async deleteItem(id: string): Promise<void> {
    const db = await this.init();
    await db.delete(this.storeName, id);
    return;
  }

  // Removes a specific document from a record in the IndexedDB.
  async removeDocument(
    id: string,
    filepath: string,
    filename: string,
  ): Promise<void> {
    const db = await this.init();
    const tx = db.transaction(this.storeName, "readwrite");
    const item = await tx.objectStore(this.storeName).get(id);

    if (!item) return;

    const existingDocs = Array.isArray(item.documents) ? item.documents : [];
    const existingFilenames = Array.isArray(item.filenames)
      ? item.filenames
      : [];
    const existingImages = Array.isArray(item.image_filenames)
      ? item.image_filenames
      : [];

    const updatedDocs = existingDocs.filter((d: string) => d !== filepath);
    const updatedFilenames = existingFilenames.filter(
      (f: string) => f !== filename,
    );
    const updatedImages = existingImages.filter((f: string) => f !== filename);

    await tx.objectStore(this.storeName).put({
      ...item,
      documents: updatedDocs,
      filenames: updatedFilenames,
      image_filenames: updatedImages,
    });
  }
}
